import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import { ChevronLeft } from "lucide-react";
import {
  PL,
  ContentSection,
  AutoTOC,
  KeyPointsBlock,
  Sources,
  Warn,
} from "../components/contentTemplate";

// Section order mirrors the 8 H2 headings of the body, plus the trailing
// Sources block. Anchor IDs are short, stable, kebab-case.
const SECTIONS = [
  { id: "concerne",            title: "Êtes-vous concerné" },
  { id: "visa",                title: "Visa, court séjour ou long séjour" },
  { id: "parcours",            title: "Le parcours selon votre motif" },
  { id: "travailler",          title: "Travailler, le permis et la réforme de 2025" },
  { id: "carte-sejour",        title: "La carte de séjour" },
  { id: "demarches-communes",  title: "Les démarches communes à tous" },
  { id: "vie-courante",        title: "Conduire, déménager, se marier, devenir polonais et payer ses impôts" },
  { id: "pieges",              title: "Les pièges et les délais propres aux pays tiers" },
];

const SOURCES = [
  { url: "https://mos.cudzoziemcy.gov.pl/en/informacje/zezw-praca_EN/objasnienie_EN",
    label: "Office des étrangers (MOS), types de permis de travail",
    description: "Source officielle. Elle décrit les six catégories de permis de A à E plus S, leurs critères et le fait qu'ils sont délivrés par le voïvode." },
  { url: "https://psz.praca.gov.pl/dla-pracodawcow-i-przedsiebiorcow/zatrudnianie-cudzoziemcow/powierzenie-pracy-na-podstawie-oswiadczenia",
    label: "Service public de l'emploi (MRPiPS), procédure par déclaration",
    description: "Source officielle. Elle établit que la procédure simplifiée concerne quatre pays, l'Arménie, la Biélorussie, la Moldavie et l'Ukraine, pour une durée maximale de 24 mois." },
  { url: "https://zielonalinia.gov.pl/oswiadczenie-o-powierzeniu-pracy-cudzoziemcowi-ktorych-krajow-dotyczy/",
    label: "Zielona Linia, retrait de la Géorgie",
    description: "Établit qu'au 1er décembre 2025 la liste est passée de cinq à quatre pays, la Géorgie en ayant été retirée, en application du règlement du 21 novembre 2025." },
  { url: "https://cudzoziemcywpracy.pip.gov.pl/images/karta_cudzoziemcy_maj.pdf",
    label: "Inspection nationale du travail (PIP), fiche sur le travail des étrangers",
    description: "Source officielle. Elle explique quels titres permettent de travailler et le principe selon lequel tout visa polonais de type C ou D, assorti de l'autorisation requise, peut ouvrir le droit au travail, sauf exceptions." },
  { url: "https://www.gov.pl/web/family/thats-a-given-labour-market-laws-will-come-into-effect-on-1-june-2025",
    label: "Ministère de la Famille, du Travail et de la Politique sociale (gov.pl), réforme au 1er juin 2025",
    description: "Source officielle. Elle fixe la date d'entrée en vigueur et les objectifs de la réforme." },
  { url: "https://www.ibanet.org/new-rules-for-hiring-foreigners-in-Poland",
    label: "International Bar Association, nouvelles règles d'embauche",
    description: "Établit la suppression du test du marché du travail, la dématérialisation, le relèvement des sanctions et les types de visas qui n'autorisent pas à travailler." },
  { url: "https://kpmg.com/xx/en/our-insights/gms-flash-alert/flash-alert-2025-140.html",
    label: "KPMG, alerte mobilité internationale sur la loi du 20 mars 2025",
    description: "Donne le nom et le périmètre de la loi ainsi que l'obligation de passer par praca.gov.pl." },
  { url: "https://progressholding.pl/en/work-permit-in-poland-2025-who-needs-it-types-procedure-costs/",
    label: "Progress Holding, types de permis et cas de dispense",
    description: "Détaille les catégories de permis et les situations dispensées de permis de travail." },
  { url: "https://www.playroll.com/work-permit-visas/poland",
    label: "Playroll, guide des permis et visas de travail en Pologne",
    description: "Décrit le titre unique séjour et travail valable jusqu'à trois ans, la voie vers la résidence permanente après cinq ans et la carte bleue européenne." },
  { url: "https://www.bestmigrationconsultant.com/blog/poland-work-visa-complete-guide/",
    label: "Best Migration, guide du visa de travail polonais",
    description: "Décrit l'enchaînement permis de travail puis visa national de type D, les délais et les rendez-vous au consulat." },
  { url: "https://warszawa19115.pl/-/exchange-of-a-foreign-driving-licence-not-specified-in-the-traffic-conventions-into-a-polish-driving-licence",
    label: "Warszawa 19115, échange d'un permis non visé par les conventions",
    description: "Établit l'obligation d'examen pour un permis venu d'un pays non signataire des conventions et le délai d'échange de 185 jours." },
  { url: "https://gdansk.pip.gov.pl/aktualnosci/od-stycznia-2026-r-wynagrodzenie-minimalne-w-gore",
    label: "Inspection du travail de Gdańsk (PIP), salaire minimum 2026",
    description: "Fixe le salaire minimum 2026 à 4 806 PLN bruts par mois, plancher qui s'applique aussi aux travailleurs étrangers." },
];

// Shared classes for inline internal links in the body.
const INTERNAL_LINK = "text-[#0056D2] hover:underline";

export default function DemarchesPaysTiers() {
  const { lp } = useT();

  return (
    <article
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
      data-testid="demarches-pays-tiers-page"
    >
      <Link
        to={lp("/demarches")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#0056D2] mb-6"
        data-testid="dpt-back"
      >
        <ChevronLeft className="w-4 h-4" />
        Retour aux Démarches
      </Link>

      {/* Hero */}
      <header className="mb-10" data-testid="dpt-hero">
        <span className="hej-chip" data-testid="dpt-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          Mis à jour en juin 2026
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          S'installer en Pologne quand on vient d'un pays tiers (hors UE)
        </h1>
      </header>

      {/* Chapeau d'introduction */}
      <section className="space-y-4 text-slate-700 leading-relaxed text-lg mb-12" data-testid="dpt-lede">
        <p>
          Cette page s'adresse aux personnes qui ne sont ni citoyennes de l'UE, ni de l'EEE, ni suisses, ainsi qu'aux Britanniques
          arrivés après le 1er janvier 2021. Pour vous, s'installer en Pologne suit un parcours plus long que pour un Européen, en
          trois temps, un visa pour entrer, une autorisation pour travailler et un titre pour rester. Si vous venez de l'UE, de l'EEE
          ou de Suisse, ou si vous êtes britannique couvert par l'accord de retrait, c'est l'autre voie qui vous concerne, décrite
          sur notre page <Link to={lp("/demarches")} className={INTERNAL_LINK}>Démarches</Link>.
        </p>
        <p>
          Cette page traite ce qui est propre aux pays tiers. Pour les formalités communes à tout le monde, comme le PESEL, la banque
          ou les impôts, elle renvoie vers la page Démarches afin de ne pas répéter l'information.
        </p>
      </section>

      {/* Sommaire auto-généré */}
      <AutoTOC sections={SECTIONS} testid="dpt-toc" />

      {/* À retenir */}
      <KeyPointsBlock testid="dpt-key-points">
        <p>
          Si vous venez d'un pays hors UE, EEE et Suisse, votre installation suit trois temps, un visa pour entrer, un permis de
          travail obtenu par votre employeur et un titre de séjour une fois sur place. Depuis la réforme de 2025, la procédure est
          plus rapide côté employeur mais plus contrôlée, avec un salaire au moins égal au minimum légal. Les vrais obstacles sont
          souvent pratiques plutôt que juridiques, le rendez-vous au consulat, les délais et le permis rattaché à un seul employeur.
        </p>
      </KeyPointsBlock>

      {/* Sections */}
      <ContentSection id="concerne" n={1} title="Êtes-vous concerné">
        <p>
          On parle de pays tiers pour toute nationalité située hors de l'Union européenne, de l'Espace économique européen et de la
          Suisse. Un Canadien, un Sénégalais, un Indien, un Marocain ou un Américain relèvent de ce régime. Un Britannique aussi,
          s'il s'installe depuis le 1er janvier 2021, puisque le Brexit lui a fait perdre la libre circulation.
        </p>
        <p>
          La conséquence est simple. Sauf cas de dispense, vous ne pouvez pas vous installer pour travailler sans qu'un employeur
          engage d'abord une procédure pour vous. C'est lui qui ouvre la porte, pas vous.
        </p>
      </ContentSection>

      <ContentSection id="visa" n={2} title="Visa, court séjour ou long séjour">
        <p>
          Deux visas, deux usages. Le visa Schengen de type C autorise un court séjour, 90 jours sur toute période de 180 jours, pour
          du tourisme, une visite ou des entretiens. Il ne permet jamais de travailler. Le visa national de type D autorise un long
          séjour, de 91 jours à un an, et c'est lui qu'il faut pour s'installer.
        </p>
        <p>
          Une distinction compte avant même de penser au travail. Certaines nationalités entrent sans visa pour un court séjour, par
          exemple les Britanniques, les Canadiens ou les Américains, qui peuvent venir prospecter ou passer un entretien dans la
          limite des 90 jours sur 180. D'autres, comme l'Inde, le Sénégal ou le Nigéria, doivent obtenir un visa Schengen de type C
          pour la moindre visite. Aucun de ces courts séjours n'autorise à prendre un emploi.
        </p>
      </ContentSection>

      <ContentSection id="parcours" n={3} title="Le parcours selon votre motif">
        <p>
          Pour le travail, l'enchaînement type est le suivant. L'employeur obtient d'abord pour vous un permis de travail auprès de
          la voïvodie. Vous demandez ensuite un visa national de type D au consulat de Pologne de votre pays. Une fois sur place, si
          votre contrat dépasse la durée du visa, vous déposez une demande de carte de séjour avant l'expiration du visa.
        </p>
        <p>
          Pour les études, vous obtenez d'abord votre admission dans un établissement, puis un visa D ou une carte de séjour pour
          études. Une inscription à temps plein ouvre par ailleurs un accès au marché du travail sans permis, ce qui aide à financer
          ses études.
        </p>
        <p>
          Pour rejoindre votre famille, le regroupement familial suppose qu'un proche réside déjà légalement en Pologne avec un titre
          suffisant, et vous obtenez une carte de séjour à ce titre.
        </p>
      </ContentSection>

      <ContentSection id="travailler" n={4} title="Travailler, le permis et la réforme de 2025">
        <p>
          Le droit polonais connaît six catégories de permis de travail, de A à E plus S. Celle qui concerne la quasi-totalité des
          installations est le permis de type A, pour un étranger employé par une entreprise établie en Pologne. Les types B à E
          visent des situations particulières, comme les membres de direction d'une société ou les travailleurs détachés par un
          employeur étranger. Le type S couvre le travail saisonnier. Dans tous les cas, c'est l'employeur qui demande le permis, et
          celui-ci reste rattaché à un poste, un lieu et une entreprise précis.
        </p>
        <p>
          Une fois en Pologne, la voie la plus pratique pour le long terme est le titre unique, la carte de séjour temporaire et de
          travail. C'est une seule décision de la voïvodie qui couvre à la fois le séjour et l'emploi, pour une durée maximale de
          trois ans.
        </p>
        <p>
          Quelques nationalités profitent d'une procédure simplifiée, dite procédure de déclaration (<PL>oświadczenie</PL>). Depuis
          le 1er décembre 2025, elle ne concerne plus que quatre pays, l'Arménie, la Biélorussie, la Moldavie et l'Ukraine. La
          Géorgie en a été retirée à cette date. Cette procédure permet de travailler jusqu'à 24 mois sans permis classique, après
          un simple enregistrement par l'employeur au bureau de l'emploi. Les Ukrainiens sous protection temporaire relèvent depuis
          fin 2025 d'un régime de notification distinct. Les ressortissants des autres pays tiers, eux, passent par le permis de
          droit commun.
        </p>
        <p>
          Il existe aussi des cas de dispense. En sont exemptés, entre autres, les titulaires d'un séjour permanent ou du statut de
          résident de longue durée de l'UE, les étudiants inscrits à temps plein, les diplômés d'études supérieures ou d'un lycée
          polonais et le conjoint d'un citoyen polonais détenant le titre de séjour adéquat. Les profils hautement qualifiés peuvent
          par ailleurs viser la carte bleue européenne, soumise à un seuil de salaire élevé.
        </p>
        <p>
          C'est ici qu'intervient la réforme entrée en vigueur le 1er juin 2025. Elle a supprimé le test du marché du travail,
          numérisé toute la procédure sur la plateforme <PL>praca.gov.pl</PL>, imposé l'égalité de rémunération et durci les
          sanctions. Le salaire ne peut pas être inférieur au minimum légal, fixé à 4 806 PLN bruts par mois en 2026, ni à celui
          d'un collègue polonais au même poste. Nous racontons cette réforme, son origine et ses conséquences dans un article dédié,{" "}
          <Link to={lp("/blog/travailler-pologne-reforme-2025")} className={INTERNAL_LINK}>
            Travailler en Pologne après la réforme de 2025
          </Link>
          . Pour situer le salaire dans un budget réel, voir{" "}
          <Link to={lp("/cout-de-la-vie")} className={INTERNAL_LINK}>Coût de la vie</Link>.
        </p>
        <p>
          Si votre projet est de créer votre propre activité plutôt que d'être salarié, les règles sont différentes et plus strictes
          pour un ressortissant de pays tiers. Elles sont traitées sur notre page{" "}
          <Link to={lp("/entrepreneuriat")} className={INTERNAL_LINK}>Entrepreneuriat</Link>.
        </p>
        <p>
          Sur le climat politique plus large de l'immigration en Pologne, voir aussi notre article{" "}
          <Link to={lp("/blog/pacte-migratoire-pologne")} className={INTERNAL_LINK}>
            Pacte migratoire de l'UE : pourquoi la Pologne refuse la relocalisation
          </Link>
          .
        </p>
      </ContentSection>

      <ContentSection id="carte-sejour" n={5} title="La carte de séjour">
        <p>
          Le permis de travail ne donne pas le droit de séjour, et le visa ne donne pas le droit de travailler. Ce sont deux actes
          distincts, et il vous faut les deux. Sur place, la carte de séjour temporaire (<PL>karta pobytu</PL>) matérialise votre
          droit de rester, le plus souvent sous la forme du titre unique séjour et travail vu plus haut. Après cinq ans de séjour
          ininterrompu, vous pouvez viser la carte de séjour permanent, puis la nationalité. Le fonctionnement détaillé de la{" "}
          <PL>karta pobytu</PL>, les pièces et les délais figurent dans la section dédiée de notre page{" "}
          <Link to={lp("/demarches")} className={INTERNAL_LINK}>Démarches</Link>.
        </p>
      </ContentSection>

      <ContentSection id="demarches-communes" n={6} title="Les démarches communes à tous">
        <p>
          Une fois installé, vous avez besoin d'un numéro PESEL pour presque tout, d'un compte bancaire et, selon votre situation,
          d'un numéro NIP. Ces démarches ne dépendent pas de votre nationalité et se déroulent comme pour un Européen. Elles sont
          expliquées en détail sur notre page <Link to={lp("/demarches")} className={INTERNAL_LINK}>Démarches</Link>, dans les
          sections PESEL, NIP et compte bancaire. Pour vous loger, voir{" "}
          <Link to={lp("/logement")} className={INTERNAL_LINK}>Logement</Link>.
        </p>
      </ContentSection>

      <ContentSection id="vie-courante" n={7} title="Conduire, déménager, se marier, devenir polonais et payer ses impôts">
        <p>
          Ces sujets concernent tout le monde, avec parfois une nuance propre aux pays tiers.
        </p>
        <p>
          Pour le permis de conduire, attention au pays qui l'a délivré. Un permis venu d'un pays ayant signé les conventions de
          Vienne ou de Genève sur la circulation routière s'échange sans examen. Un permis d'un pays non signataire oblige en
          revanche à passer l'examen théorique polonais. Dans les deux cas, l'échange doit intervenir dans les 185 jours suivant
          l'établissement de votre résidence.
        </p>
        <p>
          Pour l'importation de vos biens, un déménagement depuis un pays tiers peut bénéficier de l'exonération douanière des biens
          de déménagement, sous conditions. Pour le mariage, l'acquisition de la nationalité par reconnaissance et la résidence
          fiscale, les règles sont les mêmes que pour les autres résidents. Tous ces sujets sont traités en détail sur la page{" "}
          <Link to={lp("/demarches")} className={INTERNAL_LINK}>Démarches</Link>.
        </p>
      </ContentSection>

      <ContentSection id="pieges" n={8} title="Les pièges et les délais propres aux pays tiers">
        <p>
          Le parcours bute souvent moins sur le droit que sur la logistique. Le premier obstacle est le rendez-vous au consulat. La
          prise de créneau sur le système e-Konsulat peut être très longue dans les pays à forte demande. Le deuxième est le pouvoir
          d'appréciation du consulat, qui peut refuser un visa s'il soupçonne un risque migratoire. Le troisième tient au permis
          lui-même, rattaché à un seul employeur et à un seul poste. Changer d'entreprise oblige en général à tout recommencer. La
          leçon pratique tient en une phrase. Visez un dossier irréprochable et anticipez largement les délais.
        </p>
      </ContentSection>

      {/* Sources */}
      <Sources sources={SOURCES} testid="dpt-sources" />

      {/* Final disclaimer, style cohérent avec Demarches et Entrepreneuriat */}
      <Warn testid="dpt-warn-final">
        Cette page a une vocation d'information générale et ne remplace pas un conseil juridique individualisé. Les règles évoluent
        vite pour les pays tiers, en particulier la liste des nationalités à procédure simplifiée et les seuils. Reportez-vous aux
        sources officielles ci-dessus à la date de votre projet.
      </Warn>
    </article>
  );
}
